import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { spotifyApi } from "@/api/spotify";
import { Button } from "@/components/ui/Button";
import { Field, TextInput } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

// The OAuth callback ultimately lands on this page with `?spotify=connected`
// (the backend redirects to /settings/spotify after exchanging the code). We
// detect that on mount, fire a "Connected" toast, then strip the query so a
// reload doesn't re-toast.
const CALLBACK_PARAM = "spotify";

export function Spotify() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [savingCreds, setSavingCreds] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const settingsQ = useQuery({
    queryKey: ["spotify", "settings"],
    queryFn: spotifyApi.settings.get,
  });

  const authQ = useQuery({
    queryKey: ["spotify", "auth"],
    queryFn: spotifyApi.auth.status,
    // Keep status fresh — token may expire.
    refetchInterval: 60_000,
  });

  // ---- OAuth callback toast --------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const v = url.searchParams.get(CALLBACK_PARAM);
    if (!v) return;
    if (v === "connected") {
      toast("Spotify connected", "success");
      qc.invalidateQueries({ queryKey: ["spotify", "auth"] });
    } else if (v === "error") {
      const msg = url.searchParams.get("message");
      toast(msg ? `Spotify connection failed: ${msg}` : "Spotify connection failed", "error");
    }
    // Strip the query params so a refresh doesn't re-fire the toast.
    url.searchParams.delete(CALLBACK_PARAM);
    url.searchParams.delete("message");
    window.history.replaceState({}, "", url.toString());
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After save / clear, blank the inputs (we never echo the secret).
  useEffect(() => {
    setClientId("");
    setClientSecret("");
  }, [settingsQ.data?.configured]);

  const saveCreds = async (e: FormEvent) => {
    e.preventDefault();
    if (!clientId.trim() || !clientSecret.trim()) {
      toast("Both Client ID and Client Secret are required", "error");
      return;
    }
    setSavingCreds(true);
    try {
      await spotifyApi.settings.set({
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
      });
      toast("Spotify credentials saved", "success");
      qc.invalidateQueries({ queryKey: ["spotify", "settings"] });
      qc.invalidateQueries({ queryKey: ["spotify", "auth"] });
    } catch (err) {
      toast(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setSavingCreds(false);
    }
  };

  const clearMut = useMutation({
    mutationFn: () => spotifyApi.settings.clear(),
    onSuccess: () => {
      toast("Spotify credentials cleared", "info");
      qc.invalidateQueries({ queryKey: ["spotify", "settings"] });
      qc.invalidateQueries({ queryKey: ["spotify", "auth"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Clear failed", "error"),
  });

  const disconnectMut = useMutation({
    mutationFn: () => spotifyApi.auth.disconnect(),
    onSuccess: () => {
      toast("Disconnected from Spotify", "info");
      qc.invalidateQueries({ queryKey: ["spotify", "auth"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Disconnect failed", "error"),
  });

  const startOAuth = async () => {
    setConnecting(true);
    try {
      const { url } = await spotifyApi.auth.start();
      // Full-page navigation away to Spotify — do not setConnecting(false).
      window.location.assign(url);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Could not start OAuth", "error");
      setConnecting(false);
    }
  };

  const configured = settingsQ.data?.configured ?? false;
  const connected = authQ.data?.connected ?? false;

  // Compute the suggested redirect URI so the user can paste it verbatim into
  // the Spotify dashboard. We use the current origin — the backend handles
  // the actual callback at this path.
  const redirectUri =
    typeof window === "undefined"
      ? "<host>/api/v1/spotify/auth/callback"
      : `${window.location.origin}/api/v1/spotify/auth/callback`;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Spotify"
        description="Sync your Spotify playlists and automatically queue every album in them for download."
      />

      {/* ---- Credentials ----------------------------------------------------- */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground/70">
            Credentials
          </h2>
          {settingsQ.isLoading ? (
            <span className="text-xs text-foreground/40">Loading…</span>
          ) : configured ? (
            <span className="rounded bg-green-500/15 px-2 py-0.5 text-xs text-green-300">
              Configured
            </span>
          ) : (
            <span className="rounded bg-muted px-2 py-0.5 text-xs text-foreground/60">
              Not configured
            </span>
          )}
        </div>

        <div className="rounded border border-line bg-surface/40 px-4 py-3 text-sm text-foreground/70">
          Get these from the{" "}
          <a
            href="https://developer.spotify.com/dashboard"
            target="_blank"
            rel="noreferrer"
            className="text-accent underline-offset-2 hover:underline"
          >
            Spotify Developer Dashboard
          </a>
          . Set the redirect URI to{" "}
          <code className="rounded bg-background px-1.5 py-0.5 font-mono text-xs">
            {redirectUri}
          </code>
          .
        </div>

        <form onSubmit={saveCreds} className="space-y-4">
          <Field
            label="Client ID"
            hint={
              configured && settingsQ.data?.client_id
                ? `Currently saved: ${maskMiddle(settingsQ.data.client_id)}`
                : undefined
            }
          >
            <TextInput
              type="password"
              autoComplete="off"
              value={clientId}
              placeholder={
                configured ? "leave blank to keep current value" : "Spotify app Client ID"
              }
              onChange={(e) => setClientId(e.target.value)}
            />
          </Field>
          <Field
            label="Client Secret"
            hint={
              configured
                ? "Stored value isn't shown. Type a new pair to replace it."
                : undefined
            }
          >
            <TextInput
              type="password"
              autoComplete="off"
              value={clientSecret}
              placeholder={
                configured ? "leave blank to keep current value" : "Spotify app Client Secret"
              }
              onChange={(e) => setClientSecret(e.target.value)}
            />
          </Field>

          <div className="flex items-center justify-end gap-2 border-t border-line pt-3">
            {configured && (
              <Button
                type="button"
                variant="danger"
                onClick={() => {
                  if (
                    confirm(
                      "Remove the stored Spotify credentials? This will also disconnect any active session.",
                    )
                  )
                    clearMut.mutate();
                }}
                loading={clearMut.isPending}
              >
                Clear
              </Button>
            )}
            <Button type="submit" loading={savingCreds}>
              Save credentials
            </Button>
          </div>
        </form>
      </section>

      {/* ---- Connection ------------------------------------------------------ */}
      {configured && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground/70">
              Connection
            </h2>
            {authQ.isLoading ? (
              <span className="text-xs text-foreground/40">Loading…</span>
            ) : connected ? (
              <span className="rounded bg-green-500/15 px-2 py-0.5 text-xs text-green-300">
                Connected
              </span>
            ) : (
              <span className="rounded bg-muted px-2 py-0.5 text-xs text-foreground/60">
                Not connected
              </span>
            )}
          </div>

          {!connected ? (
            <div className="rounded border border-line bg-surface/40 p-4 text-sm">
              <p className="text-foreground/70">
                Authorise arr-in-one to read your Spotify playlists. You'll be
                redirected to Spotify and then back here.
              </p>
              <div className="mt-3">
                <Button onClick={startOAuth} loading={connecting}>
                  Connect Spotify
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded border border-line bg-surface/40 p-4 text-sm">
              <dl className="grid grid-cols-[10rem_1fr] gap-y-1 text-foreground/70">
                {authQ.data?.username && (
                  <>
                    <dt className="text-foreground/50">Account</dt>
                    <dd className="font-medium text-foreground">
                      {authQ.data.username}
                    </dd>
                  </>
                )}
                {authQ.data?.expires_at && (
                  <>
                    <dt className="text-foreground/50">Token expires</dt>
                    <dd className="text-foreground">
                      {new Date(authQ.data.expires_at).toLocaleString()}
                    </dd>
                  </>
                )}
              </dl>
              <div className="mt-4 flex gap-2">
                <Button
                  variant="danger"
                  onClick={() => {
                    if (confirm("Disconnect Spotify? Existing playlists stay, but new syncs will fail until you reconnect."))
                      disconnectMut.mutate();
                  }}
                  loading={disconnectMut.isPending}
                >
                  Disconnect
                </Button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

/** Show the first 4 + last 4 of a long-ish identifier for a "configured ✓" hint. */
function maskMiddle(s: string): string {
  if (s.length <= 10) return s;
  return `${s.slice(0, 4)}…${s.slice(-4)}`;
}
